import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { IndianRupee, Check } from "lucide-react";
import { hydratePricing } from "@/lib/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30";

export default function PricingManager() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/pricing`, { withCredentials: true });
    setCfg({
      coursePrice: data.course_price_paise / 100,
      tiers: Object.fromEntries(
        Object.entries(data.tiers).map(([k, t]) => [
          k,
          {
            name: t.name,
            price: (t.price_paise || 0) / 100,
            strike: (t.strike_paise || 0) / 100,
            course_count: t.course_count,
            active: t.active !== false,
          },
        ]),
      ),
      updated_at: data.updated_at,
    });
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/pricing`,
        {
          course_price_paise: Math.round(cfg.coursePrice * 100),
          tiers: Object.fromEntries(
            Object.entries(cfg.tiers).map(([k, t]) => [
              k,
              {
                name: t.name,
                price_paise: Math.round(t.price * 100),
                strike_paise: Math.round(t.strike * 100),
                course_count: Number(t.course_count),
                active: t.active,
              },
            ]),
          ),
        },
        { withCredentials: true },
      );
      await hydratePricing();
      toast.success("Pricing updated — live on the website now");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update pricing");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="text-sm text-slate-400">Loading pricing…</div>;

  const setTier = (id, patch) =>
    setCfg((c) => ({ ...c, tiers: { ...c.tiers, [id]: { ...c.tiers[id], ...patch } } }));

  return (
    <div data-testid="admin-pricing-manager" className="space-y-5 max-w-4xl">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        All prices are final, GST-inclusive amounts in ₹. Changes go live across the website, cart,
        checkout and student panel immediately.
      </p>

      <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5">
        <label className="block max-w-xs">
          <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
            Individual course price
          </span>
          <input
            data-testid="pricing-course-price"
            type="number"
            className={input}
            value={cfg.coursePrice}
            onChange={(e) => setCfg({ ...cfg, coursePrice: Number(e.target.value) })}
          />
        </label>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {["elite", "basic", "premium"].map((id) => {
          const t = cfg.tiers[id];
          if (!t) return null;
          const off = t.strike > 0 ? Math.round(((t.strike - t.price) / t.strike) * 100) : 0;
          return (
            <div
              key={id}
              data-testid={`pricing-card-${id}`}
              className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 space-y-3"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-semibold tracking-tight">{t.name}</h3>
                <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                  <input
                    type="checkbox"
                    data-testid={`pricing-active-${id}`}
                    checked={t.active}
                    onChange={(e) => setTier(id, { active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">
                  Display price (₹)
                </span>
                <input
                  data-testid={`pricing-price-${id}`}
                  type="number"
                  className={input}
                  value={t.price}
                  onChange={(e) => setTier(id, { price: Number(e.target.value) })}
                />
              </label>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">
                  Strikethrough price (₹)
                </span>
                <input
                  data-testid={`pricing-strike-${id}`}
                  type="number"
                  className={input}
                  value={t.strike}
                  onChange={(e) => setTier(id, { strike: Number(e.target.value) })}
                />
              </label>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">
                  Courses unlocked
                </span>
                <input
                  data-testid={`pricing-count-${id}`}
                  type="number"
                  className={input}
                  value={t.course_count}
                  onChange={(e) => setTier(id, { course_count: e.target.value })}
                />
              </label>
              <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                <IndianRupee className="h-3 w-3" /> {off}% off · saves ₹
                {Math.max(t.strike - t.price, 0).toLocaleString("en-IN")}
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="pricing-save"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-5 py-2.5 text-xs font-semibold disabled:opacity-60"
      >
        <Check className="h-3.5 w-3.5" /> {saving ? "Updating…" : "Update website pricing"}
      </button>
      {cfg.updated_at && (
        <span className="ml-3 text-[11px] text-slate-400">
          Last updated {new Date(cfg.updated_at).toLocaleString()}
        </span>
      )}
    </div>
  );
}
